import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { fetchUserInfo } from '../../utils';
import Base from '../Base';
import Icon from '../../components/common/Icon';
import NotFound from '../../components/common/NotFound';
import companyFallback from '../../assets/images/fallback/company.png';
import '../user/PublicProfile.css';
import './EventList.css';

/* Public Communities tab. Communities are companies tagged is_community that a
   Tornea admin has APPROVED (deny keeps them out of here). Each carries one public
   referral link (Insta / FB / website). Backend: GET /api/communities. */
const Communities = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchUserInfo('/communities', setUserInfo, setLoading, setError, ['selectedEventId']);
  }, []);

  const filtered = useMemo(() => {
    if (!userInfo?.communities) return null;
    const q = searchQuery.trim().toLowerCase();
    if (!q) return userInfo.communities;
    return userInfo.communities.filter((c) => (c.name?.toLowerCase() || '').includes(q));
  }, [userInfo, searchQuery]);

  return (
    <Base activeNav="home">
      <div className="pp-page">
        <div className="el-topbar">
          <button type="button" className="el-back-btn" onClick={() => navigate(-1)} aria-label="Back">
            <Icon name="angle-left" />
          </button>
          <h1 className="el-page-title">{t('communities')}</h1>
        </div>

        <div className="el-search-bar">
          <Icon name="search" className="el-search-icon" />
          <input
            type="text"
            className="el-search-input"
            placeholder={t('searchCommunities')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              type="button"
              className="el-search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear"
            >
              <Icon name="cross" />
            </button>
          )}
        </div>

        {filtered && filtered.length > 0 ? (
          <div className="el-company-grid">
            {filtered.map((c) => (
              <Link
                key={c.id}
                to={`/public-company/${c.id}`}
                className="el-company-card"
              >
                <div className="el-company-logo-tile">
                  <img src={c.photo_url || companyFallback} alt="" className="el-company-logo" />
                  {typeof c.team_count === 'number' && c.team_count > 0 && (
                    <span className="el-company-team-pill">
                      <Icon name="users-alt" pack="sr" theme="light" />
                      {c.team_count}
                    </span>
                  )}
                </div>
                <div className="el-company-meta">
                  <div className="el-company-name">{c.name}</div>
                  {c.referral_link ? (
                    <a
                      className="el-company-sub"
                      href={c.referral_link}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Icon name="link" /> {t('visit')}
                    </a>
                  ) : (
                    typeof c.team_count === 'number' && (
                      <div className="el-company-sub">
                        {c.team_count} {c.team_count === 1 ? t('team') : t('teams')}
                      </div>
                    )
                  )}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="el-empty">
            <NotFound translationKey="noCommunitiesFound" loading={loading} />
          </div>
        )}
      </div>
    </Base>
  );
};

export default Communities;
